<script setup lang="ts">
type Props = {
  imageName?: string
  isSeo?: boolean
  title?: string | null
  subtitle?: string | null
  isError?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  imageName: '',
  isSeo: false,
  title: null,
  subtitle: null,
  isError: false
})

const rc = useRuntimeConfig();
const route = useRoute();
const localePath = useLocalePath();
const { openSupport, hasSupportChannel } = useIntercomHelpers();

const { status, data } = useAuth();
const isLoggedIn = computed(() => status.value === 'authenticated');

const isSubtitle = computed(() => !props.isSeo)

const imgPath = computed(() => {
  const cdn = rc.public.wadCdnRoot || '';
  return `${cdn}/public/hero/${props.imageName}.png?fit=clip&ar=1.42&auto=format&compress=auto`;
});

const backUrl = computed(() => {
  const u: any = data.value?.data ?? data.value ?? {};
  const home = u?.home;
  return home ? localePath(home) : localePath('/');
});

const base = rc.public.wadPlatformBaseUrl || '';
const developerSignupUrl = computed(() => {
  return `${base}/developers/signup`
});

function goHome() {
  window.location.href = backUrl.value;
}

function contactSupport() {
  openSupport();
}

const postJobUrl = `${base}/company/post-job`;
</script>

<template>
  <div class="wad4-hero-section">
    <slot name="title">
      <h1 class="wad4-hero-section__title">{{ title }}</h1>
    </slot>
    <slot name="subtitle">
      <h2
          v-if="isSubtitle"
          class="wad4-hero-section__sub-title"
          :class="{ 'wad4-hero-section__sub-title--space-bottom': !isLoggedIn || isError }"
      >
        {{ subtitle }}
      </h2>
      <p
          v-else
          class="wad4-hero-section__sub-title"
          :class="{ 'wad4-hero-section__sub-title--space-bottom': !isLoggedIn }"
      >
        {{ subtitle }}
      </p>
    </slot>
    <div class="wad4-hero-section__buttons" v-if="!isLoggedIn && !isError">
      <a
          class="wad4-button-primary wad4-hero-section__button"
          :href="developerSignupUrl"
          @click.prevent="navigateTo(developerSignupUrl)"
      >
        DEVELOPER SIGNUP
      </a>
      <a
          v-if="route.path === '/videos'"
          class="wad4-button-secondary-grey wad4-hero-section__button"
          href="https://www.wearedevelopers.com/speakers/call-for-speakers"
          target="_blank"
          rel="noopener"
      >
        APPLY TO SPEAK
      </a>
      <a
          v-else
          class="wad4-button-secondary-grey wad4-hero-section__button"
          :href="postJobUrl"
      >
        POST A JOB
      </a>
    </div>
    <div class="wad4-hero-section__buttons" v-if="isError">
      <button class="wad4-button-primary wad4-hero-section__button" @click="goHome">
        Go to Home
      </button>
      <button v-if="hasSupportChannel" class="wad4-button-secondary-grey wad4-hero-section__button" @click="contactSupport">
        Contact Support
      </button>
    </div>
    <div class="wad4-hero-section__image-container">
      <img
          v-if="!isError"
          class="wad4-hero-section__image"
          :src="imgPath"
          :srcset="`${imgPath}&w=2124 2124w,
                   ${imgPath}&w=1593 1593w,
                   ${imgPath}&w=1062 1062w,
                   ${imgPath}&w=768 768w,
                   ${imgPath}&w=531 531w,
                   ${imgPath}&w=420 420w,
                   ${imgPath}&w=385 385w,
                   ${imgPath}&w=320 320w`"
          sizes="(min-width:500px) 468px, 100vw"
          alt="WeAreDevelopers"
      />
      <img
          v-else
          class="wad4-hero-section__image"
          :src="imgPath"
          :srcset="`${imgPath}&w=2124 2124w,
                   ${imgPath}&w=1593 1593w,
                   ${imgPath}&w=1062 1062w,
                   ${imgPath}&w=768 768w,
                   ${imgPath}&w=531 531w,
                   ${imgPath}&w=420 420w,
                   ${imgPath}&w=385 385w,
                   ${imgPath}&w=320 320w`"
          sizes="(min-width:500px) 530px, 100vw"
          alt="WeAreDevelopers"
      />
    </div>
  </div>
</template>

<style lang="scss">
.wad4-hero-section {
  display: grid;
  grid-template-columns:  1fr 1fr;
  grid-template-rows: 1fr fit-content(0) fit-content(0) fit-content(0) 1fr;
  column-gap: 30px;
  max-width: 1092px;
  padding: 0;
  margin: 0 auto;

  &__title {
    grid-row: 2;
    grid-column: 1;
    max-width: 100%;
    word-break: break-word;
    font-family: $wad4-default-font-sans;
    font-weight: 700;
    font-size: 46px;
    letter-spacing: -0.02em;
    line-height: 46px;
    color: $wad4-c-text-text-primary;
    margin: 0;
    padding: 0;
    @include mqBreakPoint(1000px) {
      font-size: 64px;
      line-height: 64px;
    }

    @include mqBreakPoint(768px) {
      margin-top: -50px;
    }
  }

  &__sub-title {
    grid-row: 3;
    grid-column: 1;
    color: $wad4-c-text-text-primary;
    margin: 0;
    font-family: $wad4-default-font-sans;
    font-size: 16px;
    line-height: 22px;
    font-weight: normal;
    letter-spacing: normal;
    padding-top: 18px;

    @include mqBreakPoint(1000px) {
      font-size: 20px;
      line-height: 30px;
    }

    &--space-bottom {
      padding-bottom: 18px;
    }
  }

  &__buttons {
    grid-row: 4;
    grid-column: 1;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    max-width: 400px;
  }

  &__image {
    max-height: 375px;
    object-fit: contain;
    max-width: 100%;

    &-container {
      grid-row: 1 / span 5;
      grid-column: 2;
      justify-self: stretch;
      align-self: center;
      text-align: right;
    }
  }

  &__button { width: 190px; }

  @include mqBreakPoint(0, 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: fit-content(0) fit-content(0) fit-content(0) fit-content(0);
    row-gap: 18px;

    &__title { grid-row: 1; grid-column: 1; }

    &__sub-title {
      padding: 0;
    }

    &__buttons { max-width: 400px; }
    &__button { width: 190px; }

    &__image-container {
      grid-row: 2 / span 1;
      grid-column: 1;
      justify-self: center;
    }
  }

  @include mqBreakPoint(0, 365px) {
    &__buttons {
      max-width: unset;
      margin-top: -12px;
    }
    &__button {
      width: 100%;
      margin-top: 12px;
    }
  }
}
</style>
